import React from 'react';

interface MockCalendarProps {
  showEvent?: boolean;
  highlightDate?: number;
}

const MockCalendar: React.FC<MockCalendarProps> = ({ showEvent = true, highlightDate = 15 }) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="p-4 bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-100 min-h-full">
      {/* Header */}
      <div className="text-center mb-3">
        <h1 className="text-lg font-bold text-slate-800">Welcome, John!</h1>
        <p className="text-xs text-blue-600">Seamless reservation management</p>
      </div>

      {/* Calendar Container */}
      <div className="bg-white bg-opacity-80 rounded-xl shadow p-3">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3">
          <button className="p-1.5 rounded-full bg-blue-100 text-blue-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-bold text-slate-800">January 2025</span>
          <button className="p-1.5 rounded-full bg-blue-100 text-blue-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {days.map(day => (
            <div key={day} className="text-center text-[10px] text-blue-700 font-semibold py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for start of month (Jan 2025 starts on Wednesday) */}
          {[...Array(3)].map((_, i) => (
            <div key={`empty-${i}`} className="h-10" />
          ))}

          {/* Days 1-31 */}
          {[...Array(31)].map((_, i) => {
            const day = i + 1;
            const isToday = day === highlightDate;
            const hasReservation = showEvent && (day === 15 || day === 16 || day === 20);
            const isMultiDayStart = showEvent && day === 15;
            const isMultiDayMiddle = showEvent && day === 16;

            return (
              <div
                key={day}
                className={`h-10 p-0.5 rounded-lg border transition-all cursor-pointer
                  ${isToday
                    ? 'bg-blue-100 border-blue-400 shadow'
                    : 'bg-white border-blue-100 hover:border-blue-300'
                  }
                `}
              >
                <div className="text-right">
                  <span className={`text-[10px] ${isToday ? 'text-blue-800 font-bold' : 'text-gray-700'}`}>
                    {day}
                  </span>
                </div>
                {hasReservation && (
                  <div className={`text-[8px] px-0.5 truncate mt-0.5
                    ${isMultiDayStart ? 'bg-indigo-100 text-indigo-800 rounded-l border-l-2 border-indigo-400' : ''}
                    ${isMultiDayMiddle ? 'bg-indigo-100 text-indigo-800 rounded-r' : ''}
                    ${day === 20 ? 'bg-blue-100 text-blue-800 rounded' : ''}
                  `}>
                    {day === 15 && 'Team Mtg'}
                    {day === 16 && '...'}
                    {day === 20 && 'Review'}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="mt-3 flex justify-center gap-2">
          <button className="px-3 py-1.5 text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full font-medium">
            Create Reservation
          </button>
          <button className="px-3 py-1.5 text-xs bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-full font-medium">
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default MockCalendar;
